import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CardActions,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  EmojiEvents as TrophyIcon,
  Casino as CasinoIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Autorenew as AutorenewIcon,
} from '@mui/icons-material';
import { supabase } from '../services/supabase';
import { format } from 'date-fns';

// 당첨자에게 FCM 푸시 알림 발송 (Supabase Edge Function 호출)
//
// 실패를 console 로만 흘리면 화면에는 "푸시 발송되었습니다"가 그대로 떠서
// 실제로 안 갔는데도 갔다고 오인하게 된다(엣지 함수 미배포 시 404가 대표 사례).
// → 항상 결과 객체를 돌려주고, 호출부가 화면에 사실대로 표시한다.
async function notifyWinners(entryIds) {
  const total = entryIds?.length ?? 0;
  if (total === 0) return { ok: true, sent: 0, total: 0, failures: [], error: null };
  try {
    const { data, error } = await supabase.functions.invoke('send-winner-notifications', {
      body: { entryIds },
    });
    if (error) {
      console.error('당첨 푸시 발송 실패:', error);
      return { ok: false, sent: 0, total, failures: [], error: error.message || String(error) };
    }
    if (data && data.success === false) {
      console.error('당첨 푸시 발송 실패:', data);
      return { ok: false, sent: 0, total, failures: [], error: data.error || '알 수 없는 오류' };
    }
    const sent = data?.sent ?? 0;
    const failures = data?.failures ?? [];
    console.log('당첨 푸시 결과:', data);
    return { ok: sent === total, sent, total, failures, error: null };
  } catch (err) {
    console.error('당첨 푸시 호출 실패:', err);
    return { ok: false, sent: 0, total, failures: [], error: String(err) };
  }
}

// 푸시 실패 사유를 운영자가 읽을 수 있는 문장으로 변환
function describePushFailure(push) {
  if (push.error) {
    if (/not_found|NOT_FOUND|404/i.test(push.error)) {
      return 'send-winner-notifications 엣지 함수가 배포되어 있지 않습니다. Supabase 대시보드에서 배포해주세요.';
    }
    return `푸시 서버 호출 실패 (${push.error})`;
  }
  const noToken = push.failures.filter(f => f.reason === 'no_fcm_token').length;
  const other = push.failures.length - noToken;
  const parts = [];
  if (noToken > 0) parts.push(`앱 알림 토큰 미등록 ${noToken}명`);
  if (other > 0) parts.push(`전송 오류 ${other}건`);
  return parts.join(' · ') || '알 수 없는 사유';
}

// 순금 추첨 주기 (app_config.gold_draw_cadence)
// 기획 변경으로 '매주' 가 기본이고, 과거 주기는 기록용으로만 남겨둔다.
const GOLD_CADENCE = {
  weekly: '매주 (1주 1회차)',
  biweekly: '격주 (2주 1회차)',
  monthly: '매달 1회 (4주 1회차)',
};

// 누적 응모 집계 폴백 — get_cumulative_entries RPC 가 아직 없을 때 쓰인다.
// event_entries 는 (유저, 회차) 당 1행이고 tickets_used 에 그 회차의 응모 횟수가
// 쌓이므로, 유저별로 tickets_used 를 모두 더하면 전 회차 누적 응모 수가 된다.
// 반환값: { [user_id]: 누적 응모 수 }
function buildCumulativeFallback(goldEntries) {
  const map = {};
  for (const e of goldEntries) {
    if (!e.user_id) continue;
    map[e.user_id] = (map[e.user_id] || 0) + (e.tickets_used ?? 0);
  }
  return map;
}

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function Events() {
  const [tabValue, setTabValue] = useState(0);
  const [entries, setEntries] = useState([]);
  const [winners, setWinners] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 순금 응모 탭: 회차 필터 + 체크박스 선택 상태
  const [selectedRoundId, setSelectedRoundId] = useState('');
  const [pickedEntryIds, setPickedEntryIds] = useState([]);

  // 누적 응모 — user_id -> 전 회차 누적 응모 수
  // get_cumulative_entries RPC 결과를 담고, RPC 가 없으면 로컬 집계로 채운다.
  const [cumulative, setCumulative] = useState({});
  const [cumulativeSource, setCumulativeSource] = useState('rpc'); // rpc | fallback
  const [sortByCumulative, setSortByCumulative] = useState(true);

  // 회차 전환(롤오버) — 자동(매주 금 21:00 cron)과 별개로 수동 실행 옵션
  const [rotateDialogOpen, setRotateDialogOpen] = useState(false);
  const [rotating, setRotating] = useState(false);

  // 회차 관리 탭: 상태 필터
  // 종료 회차가 계속 쌓여서 진행 중 회차가 묻히는 문제 → 기본을 '진행 중'으로 둔다.
  const [eventStatusFilter, setEventStatusFilter] = useState('active');

  // 응모 목록: 가상/실제 유저 구분 필터
  const [virtualFilter, setVirtualFilter] = useState('all'); // all | real | virtual

  // 카드 리스트는 한 번에 50건만 그리고 '더 보기' 로 늘린다
  const [entryLimit, setEntryLimit] = useState(50);

  // 순금 추첨 주기 (app_config.gold_draw_cadence) — 기획 기준값은 '매주'
  const [goldCadence, setGoldCadence] = useState('weekly');

  // 이벤트 관리 다이얼로그
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [winnersDialogOpen, setWinnersDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventWinners, setEventWinners] = useState([]);
  const [eventFormData, setEventFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    type: 'gold_bar',
    ticket_cost: 1,
    winner_count: 10,
    end_date: '',
    status: 'active',
  });

  // 통계
  const [stats, setStats] = useState({
    goldEntries: 0,
    totalWinners: 0,
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 데이터가 변경되면 유효하지 않은 선택 제거
  // 목록이 갱신되면 사라진 응모건의 선택은 해제한다
  useEffect(() => {
    if (loading || entries.length === 0) return;
    const alive = new Set(entries.filter(e => !e.is_winner).map(e => e.id));
    setPickedEntryIds(prev => prev.filter(id => alive.has(id)));
  }, [entries, loading]);

  // 회차 선택이나 응모 목록이 바뀌면 누적 응모를 다시 집계한다.
  useEffect(() => {
    if (loading) return;
    loadCumulative(selectedRoundId, entries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoundId, entries, loading]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchEntries(),
        fetchWinners(),
        fetchStats(),
        fetchEvents(),
        fetchGoldCadence(),
      ]);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const enrichEntries = async (entries) => {
    const userIds = [...new Set(entries.map(e => e.user_id).filter(Boolean))];
    const eventIds = [...new Set(entries.map(e => e.event_id).filter(Boolean))];
    let usersMap = {}, eventsMap = {};
    // in() 결과도 상한에 걸릴 수 있어 청크로 나눠 조회한다.
    for (let i = 0; i < userIds.length; i += 300) {
      const chunk = userIds.slice(i, i + 300);
      const { data: users } = await supabase
        .from('users').select('id, name, email, is_virtual').in('id', chunk);
      if (users) users.forEach(u => { usersMap[u.id] = u; });
    }
    if (eventIds.length > 0) {
      const { data: evts } = await supabase.from('events').select('id, title, type, round_no').in('id', eventIds);
      if (evts) eventsMap = Object.fromEntries(evts.map(e => [e.id, e]));
    }
    return entries.map(e => ({
      ...e,
      users: usersMap[e.user_id] || { name: '-', email: '-' },
      events: eventsMap[e.event_id] || { title: '-', type: '-' },
    }));
  };

  const fetchEntries = async () => {
    // 순금(gold_bar) 응모만 다룬다. 폐지된 치킨/커피 응모는 DB 에 보존하되 조회하지 않는다.
    // 최근 응모 순. last_entered_at 이 없던 과거 행은 created_at 으로 대체 정렬된다.
    //
    // PostgREST 는 응답 행 수에 상한(기본 1000)이 있어서, 응모가 쌓이면
    // 오래된 회차 응모가 조용히 잘려 나간다(= 어드민에서 안 보임).
    // range 로 끝까지 페이지네이션해서 전량을 가져온다.
    const PAGE = 1000;
    let all = [];
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await supabase
        .from('event_entries')
        .select('*, events!inner(type)')
        .eq('events.type', 'gold_bar')
        .order('last_entered_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })
        .range(from, from + PAGE - 1);
      if (error) throw error;
      all = all.concat(data || []);
      if (!data || data.length < PAGE) break;
    }
    setEntries(await enrichEntries(all));
  };

  const fetchWinners = async () => {
    const { data, error } = await supabase
      .from('event_entries')
      .select('*, events!inner(type)')
      .eq('events.type', 'gold_bar')
      .eq('is_winner', true)
      .order('created_at', { ascending: false });

    if (error) throw error;
    setWinners(await enrichEntries(data || []));
  };

  // 누적 응모 수 로딩. RPC 가 아직 배포되지 않았으면(PGRST202) 로컬 집계로 폴백한다.
  // p_event_id 를 넘기면 그 회차 응모자로 범위가 좁혀지고, null 이면 전체가 온다.
  const loadCumulative = async (eventId, goldEntries) => {
    const { data, error } = await supabase.rpc('get_cumulative_entries', {
      p_event_id: eventId || null,
    });
    if (!error && Array.isArray(data)) {
      setCumulative(Object.fromEntries(
        data.map(r => [r.user_id, r.total_entries ?? 0])));
      setCumulativeSource('rpc');
      return;
    }
    if (error) {
      console.warn('get_cumulative_entries RPC 사용 불가 — 로컬 집계로 폴백:', error.message || error);
    }
    setCumulative(buildCumulativeFallback(goldEntries));
    setCumulativeSource('fallback');
  };

  const fetchGoldCadence = async () => {
    const { data } = await supabase
      .from('app_config').select('value').eq('key', 'gold_draw_cadence').maybeSingle();
    if (data?.value) setGoldCadence(data.value);
  };

  const handleChangeGoldCadence = async (value) => {
    const prev = goldCadence;
    setGoldCadence(value);
    const { error } = await supabase
      .from('app_config').upsert({ key: 'gold_draw_cadence', value }, { onConflict: 'key' });
    if (error) {
      setGoldCadence(prev);
      setError(`추첨 주기 변경 실패: ${error.message}`);
      return;
    }
    setSuccess(`순금 추첨 주기를 '${GOLD_CADENCE[value]}' 로 변경했습니다. 다음 회차부터 적용됩니다.`);
  };

  const fetchEvents = async () => {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    setEvents(data || []);
  };

  const fetchStats = async () => {
    const [goldResult, winnersResult] = await Promise.all([
      supabase
        .from('event_entries')
        .select('*, events!inner(type)', { count: 'exact', head: true })
        .eq('events.type', 'gold_bar'),
      supabase
        .from('event_entries')
        .select('*, events!inner(type)', { count: 'exact', head: true })
        .eq('events.type', 'gold_bar')
        .eq('is_winner', true),
    ]);

    setStats({
      goldEntries: goldResult.count || 0,
      totalWinners: winnersResult.count || 0,
    });
  };

  // 당첨 처리 결과 + 푸시 발송 결과를 화면에 사실대로 표시한다
  const reportWinnerResult = (savedCount, push, verb = '저장') => {
    if (push.ok) {
      setSuccess(`${savedCount}명의 당첨자가 ${verb}되었고 푸시 ${push.sent}건이 발송되었습니다.`);
      setError('');
      return;
    }
    setSuccess(`${savedCount}명의 당첨자가 ${verb}되었습니다. (당첨 처리는 정상 저장됨)`);
    setError(`푸시 발송 ${push.sent}/${push.total}건 — ${describePushFailure(push)}`);
  };

  const handleOpenEventDialog = (event = null) => {
    if (event) {
      setSelectedEvent(event);
      setEventFormData({
        title: event.title,
        description: event.description || '',
        image_url: event.image_url || '',
        type: event.type,
        ticket_cost: event.ticket_cost,
        winner_count: event.winner_count,
        end_date: event.end_date ? format(new Date(event.end_date), "yyyy-MM-dd'T'HH:mm") : '',
        status: event.status,
      });
    } else {
      setSelectedEvent(null);
      setEventFormData({
        title: '',
        description: '',
        image_url: '',
        type: 'gold_bar',
        ticket_cost: 1,
        winner_count: 10,
        end_date: '',
        status: 'active',
      });
    }
    setEventDialogOpen(true);
    setError('');
  };

  const handleSaveEvent = async () => {
    if (!eventFormData.title) {
      setError('제목을 입력하세요.');
      return;
    }

    try {
      const eventData = {
        title: eventFormData.title,
        description: eventFormData.description,
        image_url: eventFormData.image_url,
        type: eventFormData.type,
        ticket_cost: parseInt(eventFormData.ticket_cost),
        winner_count: parseInt(eventFormData.winner_count),
        end_date: eventFormData.end_date || null,
        status: eventFormData.status,
      };

      if (selectedEvent) {
        const { error } = await supabase
          .from('events')
          .update(eventData)
          .eq('id', selectedEvent.id);

        if (error) throw error;
        setSuccess('이벤트가 수정되었습니다.');
      } else {
        const { error } = await supabase
          .from('events')
          .insert(eventData);

        if (error) throw error;
        setSuccess('이벤트가 생성되었습니다.');
      }

      setEventDialogOpen(false);
      fetchEvents();
    } catch (err) {
      console.error('Failed to save event:', err);
      setError('이벤트 저장에 실패했습니다.');
    }
  };

  const handleDeleteEvent = async () => {
    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', selectedEvent.id);

      if (error) throw error;

      setSuccess('이벤트가 삭제되었습니다.');
      setDeleteDialogOpen(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (err) {
      console.error('Failed to delete event:', err);
      setError('이벤트 삭제에 실패했습니다.');
    }
  };

  // 순금 응모 탭 — 체크한 응모건을 당첨 처리 (수동)
  const handleMarkSelectedWinners = async () => {
    if (pickedEntryIds.length === 0) {
      setError('체크박스를 선택해주세요.');
      return;
    }
    // 선택 건이 여러 회차에 걸쳐 있으면 의도치 않은 회차에 당첨이 찍힌다.
    const picked = entries.filter(e => pickedEntryIds.includes(e.id));
    const rounds = [...new Set(picked.map(e => e.event_id))];
    if (rounds.length > 1) {
      setError('선택한 응모가 여러 회차에 걸쳐 있습니다. 회차를 선택한 뒤 다시 시도해주세요.');
      return;
    }
    try {
      const { error: updateError } = await supabase
        .from('event_entries')
        .update({ is_winner: true })
        .in('id', pickedEntryIds);
      if (updateError) throw updateError;
      const markedIds = [...pickedEntryIds];
      const push = await notifyWinners(markedIds);
      reportWinnerResult(markedIds.length, push);
      setPickedEntryIds([]);
      fetchData();
    } catch (err) {
      console.error('Failed to mark winners:', err);
      // 정원 초과는 DB 트리거(enforce_winner_cap)가 막는다 → 사유를 그대로 노출한다.
      setError('당첨자 저장에 실패했습니다: ' + (err.message || err));
    }
  };

  // 순금 응모 탭 — 선택한 회차의 응모자 중 무작위 N명 당첨 (자동)
  const handleRandomDraw = async (count) => {
    // 회차를 고르지 않으면 "전 회차" 가 대상이 되어, 아직 진행 중인 회차까지
    // 당첨이 찍히는 사고가 났었다. 회차 선택을 필수로 막는다.
    if (!selectedRoundId) {
      setError('먼저 위에서 추첨할 회차를 선택해주세요. (전체 이벤트 보기 상태에서는 추첨할 수 없습니다)');
      return;
    }
    // 추첨은 선택한 회차 안에서 전체(실제+가상 혼합) 대상으로 한다.
    // 유저 필터·정렬은 '보기' 전용이라 추첨 범위에는 영향을 주지 않는다.
    const eventEntries = entries.filter(
      e => e.event_id === selectedRoundId && !e.is_winner);
    if (eventEntries.length === 0) {
      setError('당첨 가능한 미당첨 응모자가 없습니다.');
      return;
    }
    const drawCount = Math.min(count, eventEntries.length);
    // Fisher-Yates shuffle
    const shuffled = [...eventEntries];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    const ids = shuffled.slice(0, drawCount).map(e => e.id);
    try {
      const { error: updateError } = await supabase
        .from('event_entries')
        .update({ is_winner: true })
        .in('id', ids);
      if (updateError) throw updateError;
      const push = await notifyWinners(ids);
      reportWinnerResult(drawCount, push, '무작위 선정');
      setPickedEntryIds([]);
      fetchData();
    } catch (err) {
      console.error('Failed to random draw:', err);
      setError('무작위 추첨에 실패했습니다: ' + (err.message || err));
    }
  };

  const handleSelectEventWinners = async (event) => {
    try {
      const { data: eventEntries, error: entriesError } = await supabase
        .from('event_entries')
        .select('*')
        .eq('event_id', event.id)
        .eq('is_winner', false);

      if (entriesError) throw entriesError;

      if (!eventEntries || eventEntries.length === 0) {
        setError('응모자가 없습니다.');
        return;
      }

      // Fisher-Yates shuffle
      const shuffled = [...eventEntries];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      const selectedEventWinners = shuffled.slice(0, event.winner_count);
      const winnerIds = selectedEventWinners.map(e => e.id);

      const { error: updateError } = await supabase
        .from('event_entries')
        .update({ is_winner: true })
        .in('id', winnerIds);

      if (updateError) throw updateError;

      await supabase
        .from('events')
        .update({ status: 'winner_announced' })
        .eq('id', event.id);

      const push = await notifyWinners(winnerIds);
      reportWinnerResult(selectedEventWinners.length, push, '선정');
      fetchData();
    } catch (err) {
      console.error('Failed to select winners:', err);
      setError('당첨자 선정에 실패했습니다: ' + (err.message || err));
    }
  };

  const handleViewEventWinners = async (event) => {
    try {
      const { data, error } = await supabase
        .from('event_entries')
        .select('*')
        .eq('event_id', event.id)
        .eq('is_winner', true);

      if (error) throw error;

      setEventWinners(await enrichEntries(data || []));
      setSelectedEvent(event);
      setWinnersDialogOpen(true);
    } catch (err) {
      console.error('Failed to fetch winners:', err);
      setError('당첨자 조회에 실패했습니다.');
    }
  };

  // 당첨자 발표일 — 회차 마감 시각이 곧 추첨 시점
  const fmtDrawDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const w = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()];
    return `${d.getMonth() + 1}/${d.getDate()}(${w})`;
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { label: '진행중', color: 'success' },
      ended: { label: '종료', color: 'default' },
      winner_announced: { label: '당첨발표', color: 'primary' },
    };
    const config = statusConfig[status] || statusConfig.active;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const handleRotateRounds = async () => {
    setRotating(true);
    try {
      const { error: rpcError } = await supabase.rpc('rotate_event_rounds', { p_force: true });
      if (rpcError) throw rpcError;
      setSuccess('순금 회차를 전환했습니다. (현재 회차 마감 + 새 회차 개설)');
      setRotateDialogOpen(false);
      fetchData();
    } catch (err) {
      console.error('회차 전환 실패:', err);
      setError('회차 전환에 실패했습니다: ' + (err.message || err));
    } finally {
      setRotating(false);
    }
  };

  // 당첨자 발송 완료 토글
  const handleMarkFulfilled = async (entryId) => {
    try {
      const { error: updateError } = await supabase
        .from('event_entries')
        .update({ claim_status: 'fulfilled' })
        .eq('id', entryId);
      if (updateError) throw updateError;
      setSuccess('발송 완료 처리되었습니다.');
      fetchData();
    } catch (err) {
      console.error('Failed to mark fulfilled:', err);
      setError('발송 처리에 실패했습니다.');
    }
  };

  const getClaimStatusChip = (status) => {
    if (status === 'submitted') {
      return <Chip label="📝 정보입력완료" color="info" size="small" />;
    }
    if (status === 'fulfilled') {
      return <Chip label="🎁 발송완료" color="success" size="small" />;
    }
    return <Chip label="⏳ 입력대기" color="default" size="small" variant="outlined" />;
  };

  const winnersColumns = [
    { field: 'id', headerName: 'ID', width: 60 },
    {
      field: 'user_name',
      headerName: '사용자',
      width: 150,
      valueGetter: (value, row) => row.users?.name || '-',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <span>{params.row.users?.name || '-'}</span>
          {params.row.users?.is_virtual && (
            <Chip label="가상" size="small" color="warning" variant="outlined"
                  sx={{ height: 18, fontSize: 11 }} />
          )}
        </Box>
      ),
    },
    {
      field: 'round_no',
      headerName: '회차',
      width: 80,
      valueGetter: (value, row) => row.events?.round_no ?? null,
      renderCell: (params) =>
        params.value != null
          ? <Chip label={`${params.value}회차`} size="small" variant="outlined" />
          : '-',
    },
    {
      field: 'event_title',
      headerName: '이벤트',
      width: 140,
      valueGetter: (value, row) => row.events?.title || '-',
    },
    {
      field: 'claim_name',
      headerName: '🧾 수령인',
      width: 110,
      renderCell: (params) => (
        <Box sx={{ fontWeight: params.row.claim_name ? 700 : 400, color: params.row.claim_name ? '#000' : '#bbb' }}>
          {params.row.claim_name || '미입력'}
        </Box>
      ),
    },
    {
      field: 'claim_phone',
      headerName: '📱 휴대폰',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ fontWeight: params.row.claim_phone ? 700 : 400, color: params.row.claim_phone ? '#2D9500' : '#bbb', fontFamily: 'monospace' }}>
          {params.row.claim_phone
            ? params.row.claim_phone.replace(/^(\d{3})(\d{4})(\d{4})$/, '$1-$2-$3')
            : '미입력'}
        </Box>
      ),
    },
    {
      field: 'claim_status',
      headerName: '수령상태',
      width: 140,
      valueGetter: (value, row) => row.claim_status || 'pending',
      renderCell: (params) => getClaimStatusChip(params.row.claim_status),
    },
    {
      field: 'fulfill_action',
      headerName: '발송처리',
      width: 130,
      sortable: false,
      renderCell: (params) => {
        if (params.row.claim_status === 'fulfilled') {
          return <Chip label="✅ 완료" color="success" size="small" />;
        }
        if (params.row.claim_status === 'submitted') {
          return (
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => handleMarkFulfilled(params.row.id)}
            >
              발송완료
            </Button>
          );
        }
        return <Chip label="⏳ 대기" size="small" variant="outlined" />;
      },
    },
    {
      field: 'user_email',
      headerName: '이메일',
      width: 200,
      valueGetter: (value, row) => row.users?.email || '-',
    },
    {
      field: 'created_at',
      headerName: '당첨일시',
      width: 150,
      valueFormatter: (value) =>
        value ? format(new Date(value), 'yyyy-MM-dd HH:mm') : '',
    },
  ];

  // 회차(이벤트)별 응모 현황 집계 — 응모자 수, 사용된 응모권 합, 당첨자 수
  const roundStats = (eventId) => {
    const rows = entries.filter((e) => e.event_id === eventId);
    return {
      entrants: rows.length,
      tickets: rows.reduce((sum, e) => sum + (e.tickets_used || 0), 0),
      winners: rows.filter((e) => e.is_winner).length,
    };
  };

  // 순금 응모 목록 패널 — 회차 선택 + 누적 응모 기준 당첨자 선정
  const renderEntriesPanel = () => {
    const byEvent = selectedRoundId
      ? entries.filter(e => e.event_id === selectedRoundId)
      : entries;
    // 가상 유저는 시드 데이터라, 실제 응모자만 보고 싶을 때 걸러낼 수 있게 한다.
    const filteredEntries =
      virtualFilter === 'real'    ? byEvent.filter(e => !e.users?.is_virtual)
    : virtualFilter === 'virtual' ? byEvent.filter(e =>  e.users?.is_virtual)
    : byEvent;
    // 누적 응모 수 — RPC(또는 폴백) 집계값. 집계에 없으면 이 회차 응모 수로 대체.
    const totalOf = (row) => cumulative[row.user_id] ?? (row.tickets_used ?? 0);
    // 정렬은 '보기' 전용이라 체크박스 선정/무작위 추첨 대상에는 영향이 없다.
    const visibleRows = sortByCumulative
      ? [...filteredEntries].sort((a, b) => totalOf(b) - totalOf(a))
      : filteredEntries;
    const selectedRound = events.find(ev => ev.id === selectedRoundId);
    const drawCount = selectedRound?.winner_count || 10;
    const eligibleCount = filteredEntries.filter(e => !e.is_winner).length;
    const winnerCount = filteredEntries.filter(e => e.is_winner).length;

    return (
      <>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>회차 필터</InputLabel>
            <Select
              value={selectedRoundId}
              label="회차 필터"
              onChange={(e) => { setSelectedRoundId(e.target.value); setPickedEntryIds([]); }}
            >
              <MenuItem value="">전체 회차 보기</MenuItem>
              {goldEvents.map(ev => (
                <MenuItem key={ev.id} value={ev.id}>
                  🏆 {ev.round_no != null ? `${ev.round_no}회차` : '회차 미지정'}
                  {ev.status === 'active' ? ' · 진행 중' : ev.status === 'winner_announced' ? ' · 발표 완료' : ' · 종료'}
                  {ev.end_date ? ` · ${fmtDrawDate(ev.end_date)} 발표` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<TrophyIcon />}
            onClick={handleMarkSelectedWinners}
            disabled={!selectedRoundId || pickedEntryIds.length === 0}
          >
            선택 당첨 ({pickedEntryIds.length}명)
          </Button>

          <Button
            variant="outlined"
            startIcon={<CasinoIcon />}
            onClick={() => handleRandomDraw(drawCount)}
            disabled={!selectedRoundId || eligibleCount === 0}
          >
            무작위 {drawCount}명 추첨
          </Button>

          {!selectedRoundId && (
            <Typography variant="body2" color="warning.main">
              ⚠️ 추첨할 회차를 먼저 선택해주세요
            </Typography>
          )}

          {selectedRound && (
            <Typography variant="body2" color="text.secondary">
              응모자 {filteredEntries.length}명
              {' '}(실제 {filteredEntries.filter(e => !e.users?.is_virtual).length}
              {' / '}가상 {filteredEntries.filter(e => e.users?.is_virtual).length})
              {' · '}미당첨 {eligibleCount}명 · 당첨 {winnerCount}/{selectedRound.winner_count}명
            </Typography>
          )}

        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">정렬:</Typography>
          {[
            { key: true, label: '🔥 누적 응모 많은 순' },
            { key: false, label: '최근 응모순' },
          ].map((o) => (
            <Chip
              key={String(o.key)}
              label={o.label}
              color={sortByCumulative === o.key ? 'primary' : 'default'}
              variant={sortByCumulative === o.key ? 'filled' : 'outlined'}
              onClick={() => setSortByCumulative(o.key)}
              size="small"
            />
          ))}
          <Typography variant="caption" color="text.secondary">
            {cumulativeSource === 'rpc'
              ? '누적 응모는 전 회차 합계입니다.'
              : '※ 누적 응모는 불러온 순금 응모 내역으로 집계한 값입니다.'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">유저:</Typography>
          {[
            { key: 'all', label: '전체' },
            { key: 'real', label: '실제' },
            { key: 'virtual', label: '가상(시드)' },
          ].map((f) => {
            const n =
              f.key === 'all' ? byEvent.length
            : f.key === 'real' ? byEvent.filter(e => !e.users?.is_virtual).length
            : byEvent.filter(e => e.users?.is_virtual).length;
            return (
              <Chip
                key={f.key}
                label={`${f.label} (${n})`}
                color={virtualFilter === f.key ? (f.key === 'virtual' ? 'warning' : 'primary') : 'default'}
                variant={virtualFilter === f.key ? 'filled' : 'outlined'}
                onClick={() => setVirtualFilter(f.key)}
                size="small"
              />
            );
          })}
        </Box>

        {/* 가로 스크롤 표는 한 화면에 안 들어와 읽기 어렵다.
            당첨자 선정에 필요한 정보만 세로 카드로 보여준다. */}
        <Box sx={{ maxHeight: 620, overflowY: 'auto', pr: 1 }}>
          {loading && (
            <Typography color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
              로딩 중...
            </Typography>
          )}
          {!loading && visibleRows.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
              응모 내역이 없습니다.
            </Typography>
          )}
          {!loading && visibleRows.slice(0, entryLimit).map((row) => {
            const picked = pickedEntryIds.includes(row.id);
            const ev = row.events || {};
            const u = row.users || {};
            const total = totalOf(row);
            return (
              <Box
                key={row.id}
                onClick={() => {
                  if (row.is_winner) return;
                  setPickedEntryIds(picked
                    ? pickedEntryIds.filter(id => id !== row.id)
                    : [...pickedEntryIds, row.id]);
                }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 2, py: 1.25, mb: 1, borderRadius: 2,
                  border: '1px solid',
                  borderColor: row.is_winner ? '#FFC107' : picked ? 'primary.main' : '#E8EAEB',
                  bgcolor: row.is_winner ? '#FFFDF5' : picked ? 'rgba(45,149,0,0.06)' : '#fff',
                  cursor: row.is_winner ? 'default' : 'pointer',
                  '&:hover': { borderColor: row.is_winner ? '#FFC107' : 'primary.light' },
                }}
              >
                <input
                  type="checkbox"
                  checked={picked}
                  disabled={row.is_winner}
                  onChange={() => {}}
                  style={{ width: 18, height: 18, flexShrink: 0 }}
                />

                {/* 1행: 이름 + 가상 배지 + 당첨 배지 / 2행: 이메일 */}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
                      {u.name || '-'}
                    </Typography>
                    {u.is_virtual && (
                      <Chip label="가상" size="small" color="warning" variant="outlined"
                            sx={{ height: 18, fontSize: 11 }} />
                    )}
                    {row.is_winner && (
                      <Chip label="🏆 당첨" size="small"
                            sx={{ height: 20, fontSize: 11, bgcolor: '#FFC107', color: '#fff' }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {u.email || '-'}
                  </Typography>
                </Box>

                {/* 누적 응모(강조) + 회차 / 이번 회차 응모 */}
                <Box sx={{ textAlign: 'right', flexShrink: 0, minWidth: 160 }}>
                  <Box
                    sx={{
                      display: 'inline-flex', alignItems: 'baseline', gap: 0.5,
                      px: 1, py: 0.25, borderRadius: 1.5,
                      bgcolor: 'rgba(45,149,0,0.10)', color: '#2D9500',
                    }}
                  >
                    <Typography component="span" variant="caption" sx={{ fontWeight: 600 }}>누적</Typography>
                    <Typography component="span" sx={{ fontSize: 17, fontWeight: 800, lineHeight: 1 }}>
                      {total}
                    </Typography>
                    <Typography component="span" variant="caption" sx={{ fontWeight: 600 }}>회</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                    {ev.round_no != null ? `${ev.round_no}회차` : '회차 미지정'}
                    {' · 이번 회차 '}{row.tickets_used ?? 0}회
                    {row.is_winner && ` · ${row.claim_status === 'fulfilled' ? '발송완료'
                      : row.claim_status === 'submitted' ? '정보제출' : '정보미입력'}`}
                  </Typography>
                </Box>
              </Box>
            );
          })}
          {!loading && visibleRows.length > entryLimit && (
            <Button fullWidth sx={{ mt: 1 }}
                    onClick={() => setEntryLimit(entryLimit + 50)}>
              더 보기 ({visibleRows.length - entryLimit}건 남음)
            </Button>
          )}
        </Box>
      </>
    );
  };

  // 순금 회차 목록 — 진행 중 우선, 그 다음 최신 회차순
  const goldEvents = events
    .filter((e) => e.type === 'gold_bar')
    .slice()
    .sort((a, b) => {
      const aActive = a.status === 'active' ? 0 : 1;
      const bActive = b.status === 'active' ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      if ((a.round_no ?? 0) !== (b.round_no ?? 0)) return (b.round_no ?? 0) - (a.round_no ?? 0);
      return new Date(b.created_at) - new Date(a.created_at);
    });

  // 회차 관리 탭 목록 — 폐지된 치킨/커피 회차는 목록에 올리지 않는다(데이터는 보존).
  const visibleEvents = goldEvents.filter(
    (e) => eventStatusFilter === 'all' || e.status === 'active');

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">순금 이벤트 관리</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="warning"
            startIcon={<AutorenewIcon />}
            onClick={() => setRotateDialogOpen(true)}
          >
            다음 회차로 넘기기
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenEventDialog()}
          >
            새 순금 회차
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* 통계 카드 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 6 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="overline">순금 이벤트 응모</Typography>
              <Typography variant="h5">{stats.goldEntries}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 6 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="overline">총 당첨자</Typography>
              <Typography variant="h5">{stats.totalWinners}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Tabs
        value={tabValue}
        onChange={(e, newValue) => {
          // 탭마다 대상이 달라지므로 필터/선택은 초기화한다.
          setTabValue(newValue);
          setSelectedRoundId('');
          setPickedEntryIds([]);
        }}
      >
        <Tab label={`순금 응모 (${entries.length})`} />
        <Tab label={`당첨자 목록 (${winners.length})`} />
        <Tab label={`회차 관리 (${goldEvents.length})`} />
      </Tabs>

      {/* 탭 0: 순금 응모자 목록 + 당첨 선정 */}
      <TabPanel value={tabValue} index={0}>
        {renderEntriesPanel()}
      </TabPanel>

      {/* 탭 1: 당첨자 목록 */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ height: 600, width: '100%' }}>
          {!loading && winners.length > 0 && (
            <DataGrid
              rows={winners}
              columns={winnersColumns}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
                sorting: { sortModel: [{ field: 'round_no', sort: 'desc' }] },
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              columnVisibilityModel={{ id: false }}
            />
          )}
          {!loading && winners.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography color="text.secondary">당첨자가 없습니다.</Typography>
            </Box>
          )}
        </Box>
      </TabPanel>

      {/* 탭 2: 회차 관리 — 추첨 주기 + 순금 회차 목록 */}
      <TabPanel value={tabValue} index={2}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                🏆 순금 회차 설정
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AutorenewIcon />}
                  onClick={() => setRotateDialogOpen(true)}
                >
                  회차 수동 전환
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenEventDialog()}
                  sx={{ backgroundColor: '#2D9500', '&:hover': { backgroundColor: '#247A00' } }}
                >
                  순금 회차 추가
                </Button>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>추첨 주기</InputLabel>
                <Select
                  value={goldCadence}
                  label="추첨 주기"
                  onChange={(e) => handleChangeGoldCadence(e.target.value)}
                >
                  {Object.entries(GOLD_CADENCE).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                현재 <b>{GOLD_CADENCE[goldCadence] || goldCadence}</b> 기준입니다.
                회차 전환은 자동으로 실행되고, 주기를 바꾸면 <b>다음 회차부터</b> 적용됩니다.
                지금 바로 넘기려면 '회차 수동 전환'을 사용하세요.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">보기:</Typography>
          <Chip
            label={`진행 중 (${goldEvents.filter((e) => e.status === 'active').length})`}
            color={eventStatusFilter === 'active' ? 'primary' : 'default'}
            variant={eventStatusFilter === 'active' ? 'filled' : 'outlined'}
            onClick={() => setEventStatusFilter('active')}
            size="small"
          />
          <Chip
            label={`전체 (${goldEvents.length})`}
            color={eventStatusFilter === 'all' ? 'primary' : 'default'}
            variant={eventStatusFilter === 'all' ? 'filled' : 'outlined'}
            onClick={() => setEventStatusFilter('all')}
            size="small"
          />
        </Box>
        <Grid container spacing={3}>
          {visibleEvents.map((event) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={event.id}>
              <Card variant="outlined" sx={{ borderColor: event.status === 'active' ? '#2D9500' : undefined }}>
                {event.image_url && (
                  <Box
                    component="img"
                    src={event.image_url}
                    alt={event.title}
                    sx={{ width: '100%', height: 140, objectFit: 'cover' }}
                  />
                )}
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      {event.round_no != null && (
                        <Chip label={`${event.round_no}회차`} size="small" color="primary" />
                      )}
                      <Typography variant="h6" component="div">
                        {event.title}
                      </Typography>
                    </Box>
                    {getStatusChip(event.status)}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {event.description}
                  </Typography>
                  <Typography variant="body2">응모 비용: {event.ticket_cost}장</Typography>
                  <Typography variant="body2">당첨자 수: {event.winner_count}명</Typography>
                  {(() => {
                    // 이 회차의 실제 응모 현황 (응모자 수 / 사용된 응모권 합 / 당첨자 수)
                    const st = roundStats(event.id);
                    return (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        응모: <b>{st.entrants}</b>명 · 응모권 <b>{st.tickets}</b>장 · 당첨 <b>{st.winners}</b>명
                      </Typography>
                    );
                  })()}
                  {event.end_date && (
                    <Typography variant="body2">
                      종료일: {format(new Date(event.end_date), 'yyyy-MM-dd HH:mm')}
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <IconButton size="small" onClick={() => handleOpenEventDialog(event)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      setSelectedEvent(event);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                  {event.status === 'ended' && (
                    <Button
                      size="small"
                      startIcon={<TrophyIcon />}
                      onClick={() => handleSelectEventWinners(event)}
                    >
                      당첨자 선정
                    </Button>
                  )}
                  {event.status === 'winner_announced' && (
                    <Button
                      size="small"
                      startIcon={<TrophyIcon />}
                      onClick={() => handleViewEventWinners(event)}
                    >
                      당첨자 보기
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {visibleEvents.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary">
              {eventStatusFilter === 'active'
                ? '진행 중인 순금 회차가 없습니다.'
                : '등록된 순금 회차가 없습니다.'}
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* 회차 전환(롤오버) 확인 다이얼로그 */}
      <Dialog open={rotateDialogOpen} onClose={() => setRotateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutorenewIcon color="warning" />
            다음 회차로 넘기기
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            순금 이벤트의 <b>현재 회차를 즉시 마감</b>하고 <b>새 회차를 개설</b>합니다.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            · 진행중 회차는 '종료'로 바뀌어 당첨자 선정 대상이 됩니다.<br />
            · 새 회차가 바로 열려 사용자들이 다시 응모할 수 있습니다.<br />
            · 자동 전환(매주 금요일 21:00)과 별개로 지금 수동 실행하는 것입니다.<br />
            · 수동 전환 시 <b>매주 주기가 이 시점부터 다시 계산</b>됩니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotateDialogOpen(false)} disabled={rotating}>취소</Button>
          <Button variant="contained" color="warning" onClick={handleRotateRounds} disabled={rotating}>
            {rotating ? '처리 중...' : '회차 전환'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 이벤트 생성/수정 다이얼로그 */}
      <Dialog open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedEvent ? '이벤트 수정' : '새 이벤트'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="제목"
            value={eventFormData.title}
            onChange={(e) => setEventFormData({ ...eventFormData, title: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="설명"
            value={eventFormData.description}
            onChange={(e) => setEventFormData({ ...eventFormData, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <TextField
            fullWidth
            label="이미지 URL"
            value={eventFormData.image_url}
            onChange={(e) => setEventFormData({ ...eventFormData, image_url: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>타입</InputLabel>
            <Select
              value={eventFormData.type}
              label="타입"
              onChange={(e) => setEventFormData({ ...eventFormData, type: e.target.value })}
            >
              <MenuItem value="gold_bar">순금 이벤트</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="응모 비용 (응모권)"
            type="number"
            value={eventFormData.ticket_cost}
            onChange={(e) => setEventFormData({ ...eventFormData, ticket_cost: e.target.value })}
            margin="normal"
            inputProps={{ min: 1 }}
          />
          <TextField
            fullWidth
            label="당첨자 수"
            type="number"
            value={eventFormData.winner_count}
            onChange={(e) => setEventFormData({ ...eventFormData, winner_count: e.target.value })}
            margin="normal"
            inputProps={{ min: 1 }}
          />
          <TextField
            fullWidth
            label="종료일"
            type="datetime-local"
            value={eventFormData.end_date}
            onChange={(e) => setEventFormData({ ...eventFormData, end_date: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>상태</InputLabel>
            <Select
              value={eventFormData.status}
              label="상태"
              onChange={(e) => setEventFormData({ ...eventFormData, status: e.target.value })}
            >
              <MenuItem value="active">진행중</MenuItem>
              <MenuItem value="ended">종료</MenuItem>
              <MenuItem value="winner_announced">당첨발표</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventDialogOpen(false)}>취소</Button>
          <Button variant="contained" onClick={handleSaveEvent}>
            {selectedEvent ? '수정' : '생성'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>이벤트 삭제</DialogTitle>
        <DialogContent>
          <Typography>"{selectedEvent?.title}" 이벤트를 삭제하시겠습니까?</Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            ※ 삭제 시 관련 응모 내역도 함께 삭제됩니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button variant="contained" color="error" onClick={handleDeleteEvent}>
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      {/* 이벤트 당첨자 목록 다이얼로그 */}
      <Dialog open={winnersDialogOpen} onClose={() => setWinnersDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>당첨자 목록 - {selectedEvent?.title}</DialogTitle>
        <DialogContent>
          {eventWinners.length === 0 ? (
            <Typography color="text.secondary">당첨자가 없습니다.</Typography>
          ) : (
            <Box>
              {eventWinners.map((winner, index) => (
                <Box key={winner.id} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                  <Typography>
                    {index + 1}. {winner.users?.name} ({winner.users?.email})
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWinnersDialogOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Events;
